import type { BomTreeLeafNode, BomTreeNode, UnifiedBomRows } from './staging-bom-tree-view-model'
import type { FileToResolve } from './staging-types'

/**
 * ADR-0155 — the in-page search reinstated on Staging, reversing ADR-0124
 * D6's search clause. This module is the ENTIRE haystack/matcher/pruner: no
 * React, no page-level state, so every rule in D2/D5 is unit-testable in
 * isolation (`staging-part-search.test.ts`) without mounting `StagingPage`.
 *
 * D2 — the haystack is PART NAME ONLY. Nothing here reads
 * `matchedCatalogItem`, `sourcingOptions[].sku`, a filename, a drawing, or a
 * material. No new field is added to any staged-row type.
 *
 * D3/D6(b) — this module is deliberately NOT the place `StagingPage` reads
 * for counts, the CTA, or `selectableRowIds`. Every function here returns a
 * NEW, differently-named value; `StagingPage` is responsible for keeping its
 * own `displayed*`/unfiltered names untouched (see that file's own doc
 * comments) — this module has no opinion on which callers use its output.
 */

/** D2 — trimmed, lowercased. The one normalization every matcher shares. */
export function normalizeSearchQuery(query: string): string {
  return query.trim().toLowerCase()
}

/**
 * SUGGESTION 10 (Gate-5 review) — the SINGLE source of truth for "is a query
 * currently narrowing anything?". Before this existed, that question was
 * asked three different ways across this module, `StagingPage.tsx`, and
 * `StagingToolbar.tsx` — `normalizeSearchQuery(query) === ''` here,
 * `query.trim() !== ''` there — which agree only because
 * `normalizeSearchQuery` happens to be `trim().toLowerCase()` today. If that
 * ever changes (Unicode/diacritic normalization is the obvious next step),
 * the FILTER's notion of "active" and the DISCLOSURE gate's notion of
 * "active" could silently diverge — rows would narrow with no "showing X of
 * Y" and no clear button, i.e. silent narrowing, the one thing ADR-0155 D4
 * forbids. Every caller that needs to know "is search on?" — the matcher
 * functions below, `StagingPage.tsx`'s `hasActiveSearchQuery`, and
 * `StagingToolbar.tsx`'s `hasQuery` — reads THIS.
 */
export function isQueryActive(query: string): boolean {
  return normalizeSearchQuery(query) !== ''
}

/**
 * The one substring-match primitive every matcher in this module is built
 * from: case-insensitive (via `normalizeSearchQuery`), substring
 * (`.includes`). An empty/whitespace-only query matches everything (R5 — no
 * query means no narrowing, checked here rather than at every call site).
 * Not exported — `matchesPartName`/`matchesFilename` below are the named,
 * semantically-distinct call sites (D2 restricts what each one may read;
 * this function has no opinion on that).
 */
function substringMatch(query: string, value: string): boolean {
  const q = normalizeSearchQuery(query)
  if (q === '') return true
  return value.toLowerCase().includes(q)
}

/**
 * D2 — case-insensitive, trimmed, substring match against a single name.
 */
export function matchesPartName(query: string, name: string): boolean {
  return substringMatch(query, name)
}

/**
 * Human ruling, 2026-09-04 (relayed via the coordinator, recorded in ADR-0155
 * as a D2 exception) — a NARROW, DOCUMENTED exception to D2's name-only rule,
 * scoped to exactly one row type: needs-resolution rows (`FileToResolve`)
 * match on their `filename` field, the one identifier the user can actually
 * see on that row (there is no `name` to match — see
 * {@link filterFilesToResolveByQuery}'s doc comment for why that used to mean
 * "never matches anything"). Same normalization/substring semantics as
 * {@link matchesPartName} — no new matching rule, just a different field.
 *
 * **Do NOT reuse this for BOM leaves or the flat Identified/Matched
 * sections** — both stay strictly name-only per D2. This function's only
 * caller is {@link filterFilesToResolveByQuery}.
 */
export function matchesFilename(query: string, filename: string): boolean {
  return substringMatch(query, filename)
}

/**
 * D2 — the legacy flat Identified/Matched sections' own filter: matches
 * `part.name` only. Generic over `{ name: string }` so one implementation
 * serves both `IdentifiedPart[]` and `MatchedPart[]` — neither type's other
 * fields (`matchedCatalogItem`, `sourcingOptions[].sku`, `cadFilename`,
 * `drawingFilename`, `material`) are read, per D2's exhaustive exclusion
 * table.
 *
 * Returns the SAME array reference when the query is empty — an identity
 * short-circuit, not a semantic requirement, kept because `StagingPage`
 * memoizes off this result and an empty query is the common case.
 */
export function filterByPartName<T extends { readonly name: string }>(
  items: readonly T[],
  query: string,
): readonly T[] {
  if (normalizeSearchQuery(query) === '') return items
  return items.filter((item) => matchesPartName(query, item.name))
}

/**
 * Human ruling, 2026-09-04 — SUPERSEDES this function's first pass, which
 * hard-returned `[]` for every non-empty query on the premise that
 * `FileToResolve` carries no `name` field, only `filename` (D2 excludes
 * filenames from the part-name haystack, so the first pass treated this row
 * type as having nothing left to match). **That premise is corrected, not
 * this row type's exclusion from D2**: the ruling adds a narrow, DOCUMENTED
 * exception — needs-resolution rows match on `FileToResolve.filename`
 * specifically, per {@link matchesFilename} — because it is the one
 * identifier the user can actually see on this row, and "typing the exact
 * text on screen never finds it" was a worse user experience than a
 * one-row-type exception to name-only search. This does NOT widen D2 for any
 * other row type: BOM leaves and the flat Identified/Matched sections stay
 * strictly name-only.
 */
export function filterFilesToResolveByQuery(
  files: readonly FileToResolve[],
  query: string,
): readonly FileToResolve[] {
  if (normalizeSearchQuery(query) === '') return files
  return files.filter((file) => matchesFilename(query, file.filename))
}

/**
 * D2 — a BOM leaf matches if its OWN wire `name` matches, OR — when it
 * resolved against `identified[]`/`matched[]` — its `part.name` also
 * matches. Matching BOTH (not either-only) is deliberate: the leaf `name` is
 * the BOM cell's own text, `part.name` is the extension-stripped display
 * title, and D2 requires searching whichever the user is looking at.
 *
 * An unresolved (`partKind: null`) leaf has no `part`, so only its own
 * `name` is checked — `BomTreeLeafBase.name` is carried through
 * unconditionally by `staging-bom-tree-view-model.ts`'s `walk()` precisely so
 * this leaf is never unsearchable merely because it never resolved.
 */
export function leafMatchesQuery(leaf: BomTreeLeafNode, query: string): boolean {
  if (normalizeSearchQuery(query) === '') return true
  if (matchesPartName(query, leaf.name)) return true
  if (leaf.part && matchesPartName(query, leaf.part.name)) return true
  return false
}

/**
 * D5 — bottom-up, search-driven pruning of the BOM tree. An assembly node
 * renders iff at least one leaf beneath it, recursively, matches the query;
 * an assembly is NEVER itself a match (`BomTreeAssemblyNode.name` is not in
 * D2's haystack — typing an assembly's name does not keep its children, R2).
 * This is a DIFFERENT mechanism from `pruneEmptyAssemblies`'s STRUCTURAL
 * pruning (ADR-0142 D3(iii)) — that one runs unconditionally, upstream of
 * search, over the always-unfiltered `unifiedBomRows`; this one only ever
 * runs over a COPY produced for the currently-active query, and must never
 * replace or be conflated with it (D5's own "different mechanism, different
 * code path" instruction).
 *
 * An empty query returns the input array unchanged (identity), which is the
 * degenerate case of "at least one leaf beneath it matches" (every leaf
 * trivially matches an empty query) — expressed as an early return purely so
 * `StagingPage` isn't forced to rebuild the tree on every keystroke it never
 * fires (R5's "no query, no narrowing" baseline).
 */
function filterBomTree(nodes: readonly BomTreeNode[], query: string): readonly BomTreeNode[] {
  if (normalizeSearchQuery(query) === '') return nodes
  const result: BomTreeNode[] = []
  for (const node of nodes) {
    if (node.kind === 'part') {
      if (leafMatchesQuery(node, query)) result.push(node)
      continue
    }
    const filteredChildren = filterBomTree(node.children, query)
    if (filteredChildren.length === 0) continue
    result.push({ ...node, children: filteredChildren })
  }
  return result
}

function filterUngroupedLeaves(
  leaves: readonly BomTreeLeafNode[],
  query: string,
): readonly BomTreeLeafNode[] {
  if (normalizeSearchQuery(query) === '') return leaves
  return leaves.filter((leaf) => leafMatchesQuery(leaf, query))
}

/**
 * D5 — the single entry point `StagingPage` calls to search-filter the
 * unified BOM rows for RENDERING. Prunes `tree` per {@link filterBomTree} and
 * `ungrouped` per the same leaf predicate, independently (`ungrouped` has no
 * assembly ancestors to prune). Returns the SAME `UnifiedBomRows` reference
 * when the query is empty.
 *
 * Callers MUST NOT feed this result back into a count, the CTA, or
 * `selectableRowIds` — those stay on the UNFILTERED `unifiedBomRows` per
 * ADR-0155 D3/D6(b). This function has no way to enforce that from here; it
 * is enforced by naming discipline at the call site (`StagingPage.tsx`).
 */
export function filterUnifiedBomRows(rows: UnifiedBomRows, query: string): UnifiedBomRows {
  if (normalizeSearchQuery(query) === '') return rows
  return {
    // Gate-5 C-D — `nodeIdsByPartId` is SPREAD THROUGH UNFILTERED, deliberately.
    // It answers "which BOM positions does this `partId` occupy?", which the
    // search box does not change: a position scrolled out of view is still a
    // position, and narrowing the index would make a row-scoped delete's blast
    // radius depend on the query text. The doc above already forbids feeding
    // this result into counts/CTA/`selectableRowIds`; the index is safe to carry
    // because it is not a rendering input.
    ...rows,
    tree: filterBomTree(rows.tree, query),
    ungrouped: filterUngroupedLeaves(rows.ungrouped, query),
  }
}
